import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { ControlAttachmentOptions } from "../ControlAttachment";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { DropOverInput, DropOverInputAction } from "../DropOverInput/DropOverInput";
import {
	KeyEventArguments,
	TextInput,
	TextInputHandle
} from "../TextInput/TextInput";
import {
	useKeyboardListNavigation,
	useResizeObserver,
	useStableImperativeCallback,
	useSynchronizedRef
} from "client/Hooks";
import { Key } from "client/Misc/commonTypes";
import { localize } from "client_legacy/ndaw/culture";
import {
	BaseSuggestionBoxProps,
	SuggestedComboBoxItem,
	ComboBoxSuggestionBoxProps,
	ComboBoxSuggestionBoxHandle,
	SuggestionBox
} from "./SuggestionBox";

export type SelectedComboBoxItem<TValue,> = {
	text: string;
	value: TValue;
};

export type ComboBoxControlError = {
	message: string;
};

export type ComboBoxProps<TValue,> = {
	value: SelectedComboBoxItem<TValue> | nil;
	onChange(value: SelectedComboBoxItem<TValue> | undefined): void;
	searchTerm: string | undefined;
	onSearch(searchTerm: string | undefined): void;
	onBlur?(): void;
	trackingId: string;
	isInvalid?: boolean;
	isDisabled?: boolean;
	attachedOn?: ControlAttachmentOptions;
	placeholder?: string;
	onIsSuggestionsBoxOpenChange?(value: boolean): void;
	onErrorChange?(error: ComboBoxControlError | undefined): void;
	renderSuggestionBox: (
		props: BaseSuggestionBoxProps<TValue>,
		ref: Ref<ComboBoxSuggestionBoxHandle<TValue>>
	) => ReactElement;
};

export const ComboBox = <TValue,>(props: ComboBoxProps<TValue>): ReactElement => {
	const {
		value,
		onChange,
		searchTerm,
		onSearch,
		onBlur,
		trackingId,
		placeholder,
		isInvalid,
		isDisabled,
		attachedOn,
		onIsSuggestionsBoxOpenChange,
		onErrorChange,
		renderSuggestionBox
	} = props;

	const [hasFocus, setHasFocus] = useState<boolean>(false);
	const [areSuggestionsOpen, setAreSuggestionsOpen] = useState<boolean>(false);

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const textInputRef: RefObject<TextInputHandle | null> = useRef<TextInputHandle>(null);
	const suggestionBoxRef: RefObject<ComboBoxSuggestionBoxHandle<TValue> | null> = useRef<ComboBoxSuggestionBoxHandle<TValue>>(null);
	const shouldClearValueOnBlurRef: RefObject<boolean> = useRef<boolean>(false);
	const ignoreFocusEventRef: RefObject<boolean> = useRef<boolean>(false);
	const hasFocusRef: ReadonlyRefObject<boolean> = useSynchronizedRef(hasFocus);
	const blurCallbackTimeoutRef: RefObject<SetTimeoutHandle | null> = useRef<SetTimeoutHandle | null>(null);

	const { width } = useResizeObserver(ref);

	const text: string = isNotNilOrEmpty(searchTerm)
		? searchTerm
		: shouldClearValueOnBlurRef.current
			? ""
			: (value?.text ?? "");

	useEffect(
		(): void => {
			onIsSuggestionsBoxOpenChange?.(areSuggestionsOpen);
		},
		[areSuggestionsOpen, onIsSuggestionsBoxOpenChange]
	);

	const focusInputHandlerWithoutChangingSuggestionState = useCallback(
		(): void => {
			ignoreFocusEventRef.current = true;

			textInputRef.current
				?.focus();

			ignoreFocusEventRef.current = false;
		},
		[]
	);

	const actions = useMemo(
		(): readonly DropOverInputAction[] => {
			return [
				{
					icon: "chevron-down",
					onClick(): void {
						focusInputHandlerWithoutChangingSuggestionState();
						setAreSuggestionsOpen(x => !x);
					},
				}
			];
		},
		[focusInputHandlerWithoutChangingSuggestionState]
	);

	const searchHandler = useCallback(
		(newValue: string): void => {
			onSearch(newValue);
			setAreSuggestionsOpen(true);

			shouldClearValueOnBlurRef.current = !newValue;
		},
		[onSearch]
	);

	const focusHandler = useCallback(
		(): void => {
			if (ignoreFocusEventRef.current) {
				return;
			}

			setHasFocus(true);
			setAreSuggestionsOpen(true);
		},
		[]
	);

	const setControlError = useStableImperativeCallback(
		(shouldClearErrorWhenValid: boolean): void => {
			if (!onErrorChange) {
				return;
			}

			if (isNotNilOrEmpty(searchTerm)) {
				if (isNotNil(value)) {
					onErrorChange({
						message: localize("_ThisIsNotAValidSelection")
					});
				} else {
					onErrorChange({
						message: localize("_PleaseMakeASelection")
					});
				}
			} else if (shouldClearErrorWhenValid) {
				onErrorChange(undefined);
			}
		}
	);

	const clearControlError = useStableImperativeCallback(
		(): void => {
			onErrorChange?.(undefined);
		}
	);

	const blurCallback = useCallback(
		(): void => {
			if (shouldClearValueOnBlurRef.current) {
				shouldClearValueOnBlurRef.current = false;
				onChange(undefined);
			}

			setControlError(true);

			setAreSuggestionsOpen(false);
			onBlur?.();
		},
		[
			onBlur,
			onChange,
			setControlError
		]
	);

	const blurHandler = useCallback(
		(): void => {
			setHasFocus(false);

			// We cannot directly call blurCallback as shortly after, something else in the component
			// can gain focus (i.e. focusout (onBlur) fires first and then focusin (onFocus))
			blurCallbackTimeoutRef.current = setTimeout(
				(): void => {
					if (!hasFocusRef.current) {
						blurCallback();
					}
				},
				0
			);
		},
		[blurCallback, hasFocusRef]
	);

	useEffect(
		(): () => void => {
			return (): void => {
				if (blurCallbackTimeoutRef.current) {
					clearTimeout(blurCallbackTimeoutRef.current);
				}
			};
		},
		[]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			setControlError(false);

			return () => clearControlError();
		},
		[clearControlError, setControlError]
	);

	const stableOnSearch: typeof onSearch = useStableImperativeCallback(onSearch);

	useEffect(
		(): EffectWithDestructorResult => {
			if (isDisabled) {
				stableOnSearch(undefined);
				clearControlError();
			}
		},
		[
			clearControlError,
			isDisabled,
			stableOnSearch
		]
	);

	const closeSuggestionsHandler = useCallback(
		(): void => {
			setAreSuggestionsOpen(false);
			focusInputHandlerWithoutChangingSuggestionState();
		},
		[focusInputHandlerWithoutChangingSuggestionState]
	);

	const changeHandler = useCallback(
		(item: SelectedComboBoxItem<TValue>): void => {
			shouldClearValueOnBlurRef.current = false;
			onChange(item);
			onSearch(undefined);
			onErrorChange?.(undefined);
			closeSuggestionsHandler();
		},
		[
			onChange,
			onSearch,
			closeSuggestionsHandler,
			onErrorChange
		]
	);

	const selectFirstSuggestedItem = useCallback(
		(): void => {
			const topSuggestion: SelectedComboBoxItem<TValue> | undefined = ensureNotNil(
				suggestionBoxRef.current,
				"Suggestion box ref is not set. If using custom implementation make sure that it sets the value to the ref (eg using useImperativeHandle hook)."
			)
				.getTopSuggestion();

			if (topSuggestion) {
				changeHandler(topSuggestion);
			}
		},
		[changeHandler]
	);

	const {
		contextData,
		focusFirstItemHandler
	} = useKeyboardListNavigation({
		onFocusInput: focusInputHandlerWithoutChangingSuggestionState,
		onCancel: closeSuggestionsHandler,
		shouldCancelDirectlyFromItems: true
	});

	const textInputKeyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.ArrowDown:
					setAreSuggestionsOpen(true);

					// Delay to ensure suggestion box is rendered before focusing first item
					setTimeout(
						(): void => {
							focusFirstItemHandler();
						},
						1
					);

					return true;
				case Key.Enter:
					if (areSuggestionsOpen) {
						selectFirstSuggestedItem();
					}
					return true;
				case Key.Escape:
					closeSuggestionsHandler();
					return true;
				default:
					return false;
			}
		},
		[
			focusFirstItemHandler,
			closeSuggestionsHandler,
			selectFirstSuggestedItem,
			areSuggestionsOpen
		]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			ref={ref}
			onFocus={() => setHasFocus(true)}
			onBlur={blurHandler}
		>
			<DropOverInput
				isActive={hasFocus && !isDisabled}
				isInvalid={isInvalid}
				isDisabled={isDisabled}
				attachedOn={attachedOn}
				actions={actions}
			>
				<TextInput
					value={text}
					onChange={searchHandler}
					placeholder={placeholder}
					ref={textInputRef}
					onKeyDown={textInputKeyDownHandler}
					isBorderless
					isDisabled={isDisabled}
					onClick={() => setAreSuggestionsOpen(true)}
					onFocus={focusHandler}
				/>
			</DropOverInput>
			{isNotNil(ref.current)
				&& areSuggestionsOpen
				&& renderSuggestionBox(
					{
						target: ref.current,
						onSelectItem: changeHandler,
						keyboardNavigationContext: contextData,
						targetWidth: width,
						searchTerm: searchTerm ?? "",
						trackingId: generateTrackingId(trackingId, "__suggestions__wrapper"),
						onClose: () => setAreSuggestionsOpen(false)
					},
					suggestionBoxRef
				)
			}
		</div>
	);
};
ComboBox.displayName = "ComboBox";

ComboBox.SuggestionBox = SuggestionBox;

export type ComboBoxIntermediateState<TValue> = {
	selectedItem: SelectedComboBoxItem<TValue> | undefined;
	onSelectedItemChange(value: SelectedComboBoxItem<TValue> | undefined): void;
};

export function useComboBoxIntermediateState<TValue>(
	initialSelectedItem: SelectedComboBoxItem<TValue> | undefined
): ComboBoxIntermediateState<TValue> {
	const selectedItemRef: RefObject<SelectedComboBoxItem<TValue> | undefined>
		= useRef<SelectedComboBoxItem<TValue>>(initialSelectedItem);

	const selectedItemChangeHandler = useCallback(
		(value: SelectedComboBoxItem<TValue> | undefined): void => {
			selectedItemRef.current = value;
		},
		[]
	);

	return {
		selectedItem: selectedItemRef.current,
		onSelectedItemChange: selectedItemChangeHandler,
	};
}

export type {
	SuggestedComboBoxItem,
	ComboBoxSuggestionBoxProps,
	ComboBoxSuggestionBoxHandle
};